import {
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
  VolumeUp,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Slider,
  Typography,
} from '@mui/material';
import React from 'react';
import { useStudioUnauthenticatedContext } from '../../context/StudioContext';
import { formatTime } from '../../util/time';
import * as styles from './GlobalAudioPlayer.styles';

export const GlobalAudioPlayer: React.FC = () => {
  const { playerState } = useStudioUnauthenticatedContext();

  // Get current track info from playerState
  const currentTrack = playerState.currentTrack;

  const playingItem = playerState.playingItem;

  return (
    playingItem && (
      <Box sx={styles.container}>
        <Card sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Box sx={styles.mainContainer}>
              {/* Track Info */}
              <Box sx={styles.trackInfoContainer}>
                <Box
                  sx={{
                    ...styles.artwork,
                    backgroundImage: (() => {
                      const url =
                        currentTrack?.artworkThumbnailUrl ??
                        currentTrack?.artworkPreSignedUrl;
                      return url ? `url(${url})` : 'url(/album-cover.svg)';
                    })(),
                  }}
                />
                <Box sx={styles.trackTextContainer}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={styles.trackTitle}
                  >
                    {currentTrack?.title || '[No Track]'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentTrack?.artist || 'Unknown Artist'}
                  </Typography>
                </Box>
              </Box>

              {/* Player Controls */}
              <Box sx={styles.controlsContainer}>
                <IconButton
                  size="small"
                  sx={{ color: 'text.primary' }}
                  onClick={playerState.prev}
                  disabled={!playerState.hasPrev()}
                >
                  <SkipPrevious />
                </IconButton>

                <IconButton
                  onClick={() => playerState.togglePlayback()}
                  sx={styles.playButton}
                >
                  {playerState.playIntent ? <Pause /> : <PlayArrow />}
                </IconButton>

                <IconButton
                  size="small"
                  sx={{ color: 'text.primary' }}
                  onClick={playerState.next}
                  disabled={!playerState.hasNext()}
                >
                  <SkipNext />
                </IconButton>
              </Box>

              {/* Progress Bar */}
              <Box sx={styles.progressContainer}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.timeLabel}
                >
                  {formatTime(playerState.playbackPosition * 1000)}
                </Typography>

                <Slider
                  value={playerState.playbackPosition}
                  max={playerState.duration || 0}
                  onChange={(_, newValue) =>
                    playerState.seek(newValue as number)
                  }
                  sx={styles.progressSlider}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.timeLabel}
                >
                  {formatTime((playerState.duration || 0) * 1000)}
                </Typography>
              </Box>

              {/* Volume Control */}
              <Box sx={styles.volumeContainer}>
                <VolumeUp sx={styles.volumeIcon} />
                <Slider
                  value={playerState.volume}
                  onChange={(_, newValue) =>
                    playerState.setVolume(newValue as number)
                  }
                  sx={styles.volumeSlider}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  );
};
